import { CommandGroup, CommandItem } from "@repo/ui/components/ui/command";
import { Field, FieldLabel } from "@repo/ui/form-fields";
import { useLoincCodeSearch } from "@/shared/hooks/use-loinc-code-search";
import { SearchPicker } from "@/shared/pickers/search-picker";

interface LoincCodePickerProps {
  value?: { code: string; display: string };
  onChange: (value: { code: string; display: string } | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function LoincCodePicker({
  value,
  onChange,
  disabled,
  placeholder = "Search LOINC codes...",
}: LoincCodePickerProps) {
  return (
    <Field>
      <FieldLabel>
        LOINC Code <span className="text-muted-foreground font-normal">optional</span>
      </FieldLabel>
      <SearchPicker.Root placeholder={placeholder}>
        <SearchPicker.Trigger
          label={value ? `${value.code} — ${value.display}` : undefined}
          onClear={value ? () => onChange(undefined) : undefined}
          clearLabel="Clear LOINC code"
          disabled={disabled}
        />
        <SearchPicker.Content>
          <LoincResults value={value} onChange={onChange} />
        </SearchPicker.Content>
      </SearchPicker.Root>
    </Field>
  );
}

function LoincResults({ value, onChange }: LoincCodePickerProps) {
  const { state, actions } = SearchPicker.useContext();
  const { options, isLoading } = useLoincCodeSearch(state.query);
  return (
    <SearchPicker.Search>
      <SearchPicker.Results isLoading={isLoading} count={options.length} emptyMessage="No codes found.">
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.code}
              value={option.code}
              onSelect={() => {
                onChange({ code: option.code, display: option.display });
                actions.close();
              }}
              data-checked={value?.code === option.code ? "true" : undefined}
            >
              <div className="flex flex-col">
                <span className="text-sm">{option.display}</span>
                <span className="text-xs text-muted-foreground">{option.code}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </SearchPicker.Results>
    </SearchPicker.Search>
  );
}
